import React, { useEffect, useState } from 'react';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { DataStore, Hub, Auth } from 'aws-amplify';
import { Message, SendPicture, GroupConversation } from '../models';
import { MaterialCommunityIcons, FontAwesome, AntDesign, Ionicons } from '@expo/vector-icons';
import * as CmnUtil from '../components/CmnUtil';
import * as AppConst from '../components/AppConst';
import Dashboard from './dashboard/Dashboard';
import GroupList from './group/GroupList';
import UserList from './user/UserList';
import ChangePassword from './password/ChangePassword';
import ChatList from './chat/ChatNavigator';

/** ================================
*  メイン画面
*  ================================*/
const AppMain = () => {
  // 画面定義
  const screenType = {
    dashboard: 'dashboard',
    group: 'group',
    user: 'user',
    chat: 'chat',
    password: 'password',
  }

  // 描画準備
  const [ready, setReady] = useState(false);
  // 画面遷移
  const [menu, setMenu] = useState(screenType.dashboard);

  // サインアウト
  const [signOutFlag, setSignOut] = useState<boolean>(false);

  // 未読数
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    CmnUtil.startLoading();
    const datastore = Hub.listen('datastore', (data) => {
      if (data.payload.event === 'ready') {
        setReady(true);
        CmnUtil.endLoading();
        datastore();
      }
    });
    DataStore.start();
  }, []);

  useEffect(() => {
    if (ready) {
      // 未読数の集計
      countUnread();

      // チャット情報の購読
      const groupConversation = DataStore.observe(GroupConversation).subscribe(countUnread);
      const message = DataStore.observe(Message).subscribe(countUnread);
      const sendPicture = DataStore.observe(SendPicture).subscribe(countUnread);
      return () => {
        groupConversation.unsubscribe();
        message.unsubscribe();
        sendPicture.unsubscribe();
      }
    }
  }, [ready]);

  // 未読数の集計
  const countUnread = async () => {
    let tmpUnread = 0;

    const appUser = await CmnUtil.getCurrentUser();
    const groupConversations = (await DataStore.query(GroupConversation)).filter(c => c.group.id === appUser?.group.id);
    for (let i = 0; i < groupConversations.length; i++) {
      const groupConversation = groupConversations[i];
      // 未読メッセージ数取得
      const message = (await DataStore.query(Message)).filter(c => c.conversation.id === groupConversation.conversation.id && c.sender.id != appUser?.id && c.isRead == false);
      const sendPicture = (await DataStore.query(SendPicture)).filter(c => c.conversation.id === groupConversation.conversation.id && c.sender.id != appUser?.id && c.isRead == false);
      tmpUnread = tmpUnread + message.length + sendPicture.length;
    }
    setUnread(tmpUnread);
  }

  // 削除
  const signOut = async () => {
    try {
      await Auth.signOut();
    } catch (err: any) {
      console.log(err);
    }
    location.reload();
  }

  return (
    <View style={{ flexDirection: 'row', flex: 1 }}>
      {ready && <>
        {/* メニュー */}
        <View style={{ width: 200 }}>
          <Text style={[AppConst.styles.fontSizeS, styles.menu_title]}>Main</Text>
          <TouchableOpacity style={[styles.menu_bar, menu == screenType.dashboard ? styles.menu_selected : {}]} onPress={() => { setMenu(screenType.dashboard); }}>
            <AntDesign name='dashboard' size={20} />
            <Text style={styles.menu_text}>ダッシュボード</Text>
          </TouchableOpacity>
          <TouchableOpacity style={[styles.menu_bar, menu == screenType.group ? styles.menu_selected : {}]} onPress={() => { setMenu(screenType.group); }}>
            <FontAwesome name='group' size={20} />
            <Text style={styles.menu_text}>組織管理</Text>
          </TouchableOpacity>
          <TouchableOpacity style={[styles.menu_bar, menu == screenType.user ? styles.menu_selected : {}]} onPress={() => { setMenu(screenType.user); }}>
            <AntDesign name='user' size={20} />
            <Text style={styles.menu_text}>ユーザー管理</Text>
          </TouchableOpacity>
          <TouchableOpacity style={[styles.menu_bar, menu == screenType.chat ? styles.menu_selected : {}]} onPress={() => { setMenu(screenType.chat); }}>
            <Ionicons name='chatbox-ellipses-outline' size={20} />
            <Text style={styles.menu_text}>チャット</Text>
            <CmnUtil.Badge count={unread} />
          </TouchableOpacity>

          <Text style={[AppConst.styles.fontSizeS, styles.menu_title, { marginTop: 50 }]}>Account</Text>
          <TouchableOpacity style={[styles.menu_bar, menu == screenType.password ? styles.menu_selected : {}]} onPress={() => { setMenu(screenType.password); }}>
            <MaterialCommunityIcons name='form-textbox-password' size={20} />
            <Text style={styles.menu_text}>パスワード変更</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.menu_bar} onPress={() => { setSignOut(true); }}>
            <FontAwesome name='sign-out' size={20} />
            <Text style={styles.menu_text}>サインアウト</Text>
          </TouchableOpacity>
        </View>

        {/* 操作画面 */}
        <View style={{ flex: 1, backgroundColor: AppConst.defaultBaseColor }}>
          {menu == screenType.dashboard && <Dashboard />}
          {menu == screenType.group && <GroupList />}
          {menu == screenType.user && <UserList />}
          {menu == screenType.chat && <ChatList />}
          {menu == screenType.password && <ChangePassword />}
        </View>

        {/* サインアウトダイアログ */}
        {signOutFlag && <View style={AppConst.styles.dialog}>
          <View style={AppConst.styles.dialogContents}>
            <View style={{ backgroundColor: AppConst.defaultBaseColor, width: 300, borderRadius: 10 }}>
              <AppConst.DialogHeader closeFunction={() => { setSignOut(false) }} />

              <View style={{ padding: 20 }}>
                <Text>サインアウトしますか？</Text>
              </View>
              <View style={{ flexDirection: 'row', height: 40, borderTopWidth: 1, borderColor: '#707070' }}>
                <TouchableOpacity style={{ flex: 1, alignItems: 'center', justifyContent: 'center', borderRightWidth: 1, borderColor: '#707070' }} onPress={() => { setSignOut(false) }}>
                  <Text>いいえ</Text>
                </TouchableOpacity>
                <TouchableOpacity style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }} onPress={() => { signOut() }}>
                  <Text>はい</Text>
                </TouchableOpacity>
              </View>
            </View>
          </View>
        </View>}
      </>}
    </View>
  );
}

const styles = StyleSheet.create({
  menu_bar: {
    height: 50,
    alignItems: 'center',
    marginLeft: 10,
    flexDirection: 'row',
    borderBottomWidth: 2,
    borderColor: AppConst.defaultBaseColor,
  },
  menu_selected: {
    borderRightWidth: 3,
    borderRightColor: AppConst.defaultColor,
  },
  menu_text: {
    marginLeft: 5,
  },
  menu_title: {
    color: '#707070',
  },
});

export default AppMain;
